import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import set from "lodash/set";
import get from "lodash/get";
import { CalendarDateTime, startOfYear } from "@internationalized/date";
import debounce from "lodash/debounce";
import moment, { Moment } from "moment-timezone";
import { trackEvent } from "../../analytics";

import { DatePicker } from "../../../components/DatePicker";
import CustomSelect from "../../../components/customSelect";
import CheckboxDropdown from "../../../components/CheckboxDropdown";

import {
  renderOrganizationOptions,
  renderVendorOptions,
} from "../utils";
import {
  organizationStore,
  useOrganizationStore,
} from "../../../store/organization";
import { eventStore, useEventStore } from "../../../store/event";
import { VENDORS_LIST_QUERY } from "../../vendors/queries";
import { createApolloClient } from "../../../providers/ApolloClientFactory";
import { Vendor } from "../../../types/vendor";
import { userStore } from "../../../store/user";
import { reportFiltersStore } from "../../../store/reportFilters";
import { GET_LOCATIONS_REPORT_FILTER} from "../../locations/queries";

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDateForApi = (date: Moment) => {
  return moment.tz(date, timezone).format("YYYY-MM-DDTHH:mm:ss");
};

interface IReportFiltersProps {
  includeLocations?: boolean;
  includeDates?: boolean;
  includeDateOnly?: boolean;
  includeDatesStartOfYear?: boolean;
  includeDatesLast90Days?: boolean;
  includeTime?: boolean;
  includeEvents?: boolean;
  multipleEvents?: boolean;
  includeVendors?: boolean;
  /**
   * Adds an "Exclude Vendor" multi-select beside the Vendor filter. Opt-in, so
   * reports that do not want a second vendor control are unaffected.
   *
   * It is independent of the Vendor filter. Set both and the report keeps the
   * vendors you included and then drops the ones you excluded, which is how
   * Hasura reads _in and _nin on the same column.
   */
  includeVendorExclusions?: boolean;
  eventScopedVendors?: boolean;
  includeUsernames?: boolean;
  includeProductTypes?: boolean;
  /** Title for the product-type filter. The ticketing Item Report calls it "Item Category" (RP-2560). */
  productTypeLabel?: string;
  includeOrganizations?: boolean;
  multipleOrganizations?: boolean;
  includeGrouping?: boolean;
  includeSubEvents?: boolean;
  includePaymentProcessors?: boolean;
  usernameOptions?: { value: string; label: string }[];
  productTypeOptions?: { value: string; label: string }[];
  locationOptions?: { value: string; label: string }[];
  paymentProcessorOptions?: { value: string; label: string }[];
  groupingOptions?: { value: string; label: string }[];
  groupingValue?: string;
  onGroupingChange?: (value: string) => void;
  onPaymentProcessorChange?: (value: string[] | null) => void;
  selectedPaymentProcessors?: string[] | null;
  vendor_id?: number;
  customQueryPaths?: Record<string, string>;
  trailingSlot?: React.ReactNode;
}

const mappings = {
  organizationId: "organization_id._eq",
  locationId: "location_id._eq",
  eventId: "event_id._eq",
  vendorId: "vendor_id._eq",
  // Lets an exclusion survive a reload — parseSearch reads it back off the URL.
  excludedVendorId: "vendor_id._nin",
  username: "clerk_id._eq",
  dateFrom: "_and.[0].transaction_hour._gte",
  dateTo: "_and.[0].transaction_hour._lte",
};

const parseSearch = (search: string) => {
  const defaults: Record<string, any> = {};
  const searchUrl = new URL(search);
  const where = searchUrl.searchParams.get("where");
  const whereVariable = JSON.parse(where ?? "{}");
  Object.entries(mappings).forEach(([key, value]) => {
    defaults[key] = get(whereVariable, value);
  });
  return defaults;
};
const { getState } = organizationStore;
const client = createApolloClient();

export const ReportFilters = ({
                                includeLocations,
                                includeDates,
                                includeDateOnly,
                                includeDatesStartOfYear,
                                includeDatesLast90Days,
                                includeTime,
                                includeEvents,
                                multipleEvents,
                                includeVendors,
                                includeVendorExclusions,
                                eventScopedVendors,
                                includeUsernames,
                                includeProductTypes,
                                productTypeLabel = "Product Type",
                                includeOrganizations,
                                multipleOrganizations,
                                includeGrouping,
                                includeSubEvents,
                                includePaymentProcessors,
                                usernameOptions,
                                productTypeOptions,
                                locationOptions,
                                paymentProcessorOptions,
                                groupingOptions,
                                groupingValue,
                                onGroupingChange,
                                onPaymentProcessorChange,
                                selectedPaymentProcessors,
                                vendor_id,
                                customQueryPaths,
                                trailingSlot,
                              }: IReportFiltersProps) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  // RP-1937: the address loses its filter when the report is opened from the sidebar
  // (including clicking the report you're already on). Watching for that lets the
  // effect below put the current filter back, so the data always matches the date boxes.
  const routerLocation = useLocation();
  const hasWhereInUrl = new URLSearchParams(routerLocation.search).has("where");
  const { getState } = userStore;
  const { getState: getEventState } = eventStore;
  const rawOrganizationId = useOrganizationStore((state) => state.organizationId);
  const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
  const eventId = useEventStore((state) => state.eventId);
  const user = getState().user;
  const allEvents = useEventStore((state) => state.allEvents);

  const allOrganizations = useOrganizationStore(
      (state) => state.allOrganizations
  );
  const organizations = useOrganizationStore((state) => state.organizations);
  const momentDateOnly = moment().add(1, "day");
  const momentDate = moment().startOf("day");
  const momentDateToFiveAm = moment()
      .startOf("day")
      .add(1, "day")
      .add(5, "hours");
  const momentDateFiveAm = moment().startOf("day").add(5, "hours");

  const dateOnly = new CalendarDateTime(
      momentDateOnly.year(),
      momentDateOnly.month() + 1,
      momentDateOnly.date()
  );
  const date = new CalendarDateTime(
      momentDate.year(),
      momentDate.month() + 1,
      momentDate.date(),
      momentDate.hour(),
      momentDate.minute(),
      momentDate.second()
  );
  const dateToFiveAm = new CalendarDateTime(
      momentDateToFiveAm.year(),
      momentDateToFiveAm.month() + 1,
      momentDateToFiveAm.date(),
      momentDateToFiveAm.hour(),
      momentDateToFiveAm.minute(),
      momentDateToFiveAm.second()
  );
  const dateFiveAm = new CalendarDateTime(
      momentDateFiveAm.year(),
      momentDateFiveAm.month() + 1,
      momentDateFiveAm.date(),
      momentDateFiveAm.hour(),
      momentDateFiveAm.minute(),
      momentDateFiveAm.second()
  );

  const momentDateLast90 = moment().startOf("day").subtract(90, "days");
  const dateLast90 = new CalendarDateTime(
      momentDateLast90.year(),
      momentDateLast90.month() + 1,
      momentDateLast90.date()
  );

  const defaults = parseSearch(window.location.href);
  const firstFilterEffectRef = useRef(true);
  const debouncedTrackFilterChange = useMemo(
    () =>
      debounce((payload: Record<string, unknown>) => {
        trackEvent("filter_change", { payload });
      }, 1000),
    []
  );
  const [dateFromFiveAm, setdateFromFiveam] = useState(dateFiveAm);
  const [dateOnlyFrom, setDateOnlyFrom] = useState(startOfYear(dateOnly));
  const [dateOnlyTo, setDateOnlyTo] = useState(dateOnly);
  const [dateFrom, setDateFrom] = useState(
      includeDatesLast90Days
          ? dateLast90
          : includeDatesStartOfYear
              ? startOfYear(dateFiveAm)
              : dateFiveAm
  );
  const [dateFromStartOfYear, setDateFromStartOfYear] = useState(
      startOfYear(dateFiveAm)
  );
  const [dateTo, setDateTo] = useState(dateToFiveAm);
  const [location, setLocation] = useState(defaults.locationId);
  const [event, setEvent] = useState(defaults.eventId);
  const [vendor, setVendor] = useState(defaults.vendorId);
  const [excludedVendor, setExcludedVendor] = useState(defaults.excludedVendorId);
  const [username, setUsername] = useState(defaults.username);
  // Product Type filter (multi-select). Mirrors the username pattern: derives
  // its options from data the parent component already has, persists to the
  // URL so refresh preserves it.
  const [productType, setProductType] = useState(defaults.productType);
  const [organization, setOrganization] = useState<string[] | null>(
      defaults.organizationId || organizationId
  );
  const [subEvent, setSubEvent] = useState<string | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const fetchVendor = async () => {

    let where: any = (organizationId && organizationId !== 0)
        ? {
          organization_id: {
            _eq: organizationId,
          },
        }
        : {};
    let locationWhere: any = (organizationId && organizationId !== 0)
        ? {
          event: {organization_id: {
              _eq: organizationId,
            }},
        }
        : {};
    if (eventId) {
      locationWhere = {
        ...locationWhere,
        event_id: {
          _eq: eventId,
        },
      };
    }
    if (user?.vendors) {
      locationWhere = {
        ...locationWhere,
        vendor_id: {
          _in: user?.vendors,
        },
      };
      where = {
        ...where,
        id: {
          _in: user?.vendors,
        }
      }
    }
    if (eventScopedVendors && eventId) {
      where = {
        ...where,
        locations: { event_id: { _eq: eventId } },
      };
    }
    if (user?.events) {
      locationWhere = {
        ...locationWhere,
        event_id: {
          _in: user?.events,
        },
      };
    }

    // RP-2387: filter selectors only offer ACTIVE vendors/locations. These are
    // pure filter dropdowns (no saved selection to preserve), so active-only is safe.
    where = { ...where, is_active: { _eq: true } };
    locationWhere = { ...locationWhere, is_active: { _eq: true } };

    const [{ data: vendorData }, {data: locationData}] = await Promise.all ([
      client.query({
        query: VENDORS_LIST_QUERY,
        variables: {
          where,
        },
      }),
      client.query({
        query: GET_LOCATIONS_REPORT_FILTER,
        variables: {
          locationWhere,
        },
      }),
    ]);
    const fetchedVendors = vendorData?.vendors || [];
    const fetchedLocations = locationData?.locations || [];
    setVendors(fetchedVendors);
    setLocations(fetchedLocations);
    const { setAllVendors, setAllLocations } = reportFiltersStore.getState();
    setAllVendors(fetchedVendors);
    setAllLocations(fetchedLocations);
  };
  useEffect(() => {
    fetchVendor();
  }, [organizationId,eventId]);

  // Refresh events data when sub-events filter is enabled
  useEffect(() => {
    if (includeSubEvents) {
      getEventState().fetchAllEvents();
    }
  }, []);

  // Reset sub-event when event changes
  useEffect(() => {
    setSubEvent(null);
  }, [event, eventId]);

  // Derive sub-event options from selected event, global event, or all org events
  const effectiveEventId = event || eventId;
  const subEventOptions = (() => {
    if (effectiveEventId) {
      const selectedEventData = allEvents?.find((e: any) => e.id === effectiveEventId);
      return [...(selectedEventData?.sub_events || [])]
        .sort((a: any, b: any) => b.start_date.localeCompare(a.start_date))
        .map((se: any) => ({
          label: `${se.name} (${se.start_date} - ${se.end_date})`,
          value: se.id,
        }));
    }
    // No event selected — show all sub-events across all events in the org
    const all = (allEvents || []).flatMap((e: any) =>
      (e.sub_events || []).map((se: any) => ({
        se,
        event: e,
      }))
    );
    all.sort((a: any, b: any) => b.se.start_date.localeCompare(a.se.start_date));
    return all.map(({ se, event }: any) => ({
      label: `${event.name} — ${se.name} (${se.start_date} - ${se.end_date})`,
      value: se.id,
    }));
  })();

  useEffect(() => {
    const where: any = {};
    if (organizationId) {
      where.organization_id = {
        _eq: organizationId,
      };
    }
    if (vendor_id) {
      where.vendor_id = {
        _eq: vendor_id,
      };
    } else if (includeVendors && user?.vendors) {
      where.vendor_id = {
        _in: user?.vendors,
      };
    }

    if (includeOrganizations) {
      delete where.organization_id;
      where.organizations = organization;
    }

    if (includeDates && includeDateOnly) {
      let dateFromFilter = {
        day: dateFrom.day,
        month: dateFrom.month - 1,
        year: dateFrom.year,
        hour: 0,
        minute: 0,
      };

      let dateToFilter = {
        day: dateTo.day,
        month: dateTo.month - 1,
        year: dateTo.year,
        hour: 0,
        minute: 0,
      };
      if (includeTime) {
        dateFromFilter = {
          ...dateFromFilter,
          hour: dateFrom.hour,
          minute: dateFrom.minute,
        };
        dateToFilter = {
          ...dateToFilter,
          hour: dateTo.hour,
          minute: dateTo.minute,
        };
      }

      if (customQueryPaths && customQueryPaths.dateFrom) {
        set(where, customQueryPaths.dateFrom, {
          _gte: formatDateForApi(moment(dateFromFilter)),
        });
      }

      if (customQueryPaths && customQueryPaths.dateTo) {
        set(where, customQueryPaths.dateTo, {
          _lt: formatDateForApi(moment(dateToFilter)),
        });
      }

      if (!customQueryPaths) {
        where["_and"] = [
          {
            transaction_hour: {
              _gte: formatDateForApi(moment(dateFromFilter)),
            },
          },
          {
            transaction_hour: {
              _lt: formatDateForApi(moment(dateToFilter)),
            },
          },
        ];
      }
    }

    if (includeDates && includeTime) {
      let dateFromFilter = {
        day: dateFrom.day,
        month: dateFrom.month - 1,
        year: dateFrom.year,
        hour: 0,
        minute: 0,
      };

      let dateToFilter = {
        day: dateTo.day,
        month: dateTo.month - 1,
        year: dateTo.year,
        hour: 0,
        minute: 0,
      };
      if (includeTime) {
        dateFromFilter = {
          ...dateFromFilter,
          hour: dateFrom.hour,
          minute: dateFrom.minute,
        };
        dateToFilter = {
          ...dateToFilter,

          hour: dateTo.hour,
          minute: dateTo.minute,
        };
      }

      if (customQueryPaths && customQueryPaths.dateFrom) {
        set(where, customQueryPaths.dateFrom, {
          _gte: formatDateForApi(moment(dateFromFilter)),
        });
      }

      if (customQueryPaths && customQueryPaths.dateTo) {
        set(where, customQueryPaths.dateTo, {
          _lt: formatDateForApi(moment(dateToFilter)),
        });
      }

      if (!customQueryPaths) {
        where["_and"] = [
          {
            transaction_hour: {
              _gte: formatDateForApi(moment(dateFromFilter)),
            },
          },
          {
            transaction_hour: {
              _lt: formatDateForApi(moment(dateToFilter)),
            },
          },
        ];
      }
    }

    if (username && !username.includes("") && username.length > 0) {
      where["username"] = {
        _in: username,
      };
    }

    if (productType && !productType.includes("") && productType.length > 0) {
      where["product_type"] = {
        _in: productType,
      };
    }

    if (event) {
      if (customQueryPaths && customQueryPaths.event) {
        where[customQueryPaths.event] = event;
      } else {
        where["event_id"] = {
          _eq: event,
        };
      }
    }

    if (vendor && !vendor.includes("") && vendor.length > 0) {
      where["vendor_id"] = {
        ...(where["vendor_id"] ?? {}),
        _in: vendor,
      };
    }

    // Exclusion is additive, not a replacement: keep whatever _in is already
    // there (an explicit Vendor pick, or the caller's own vendor scope) and add
    // _nin on top. Hasura reads the two together as AND.
    if (
      includeVendorExclusions &&
      excludedVendor &&
      !excludedVendor.includes("") &&
      excludedVendor.length > 0
    ) {
      where["vendor_id"] = {
        ...(where["vendor_id"] ?? {}),
        _nin: excludedVendor,
      };
    }

    if (location && !location.includes("") && location.length > 0) {
      where["location_id"] = {
        _in: location,
      };
    }

    // Sub-event date range filter — takes priority over regular date filters
    if (subEvent) {
      // Search across all events to find the selected sub-event
      const selectedSe = (allEvents || []).flatMap((e: any) => e.sub_events || []).find((se: any) => se.id === subEvent);
      if (selectedSe) {
        const dateField = customQueryPaths?.dateFrom
          ? customQueryPaths.dateFrom.replace(/^_and\.\[\d+\]\./, "").replace(/\._gte$/, "").split(".").pop() || "transaction_hour"
          : "transaction_hour";

        let subEventConditions;
        if (includeDateOnly && !includeTime) {
          // Date-only reports: strip times, use _gte/_lt (end + 1 day) to match date field comparison
          const seStart = moment(selectedSe.start_date, "YYYY-MM-DD").startOf("day");
          const seEnd = moment(selectedSe.end_date, "YYYY-MM-DD").startOf("day").add(1, "day");
          subEventConditions = [
            { [dateField]: { _gte: formatDateForApi(seStart) } },
            { [dateField]: { _lt: formatDateForApi(seEnd) } },
          ];
        } else {
          // Timestamp reports: use full date+time with _gte/_lt to match regular filter behavior.
          // start_time/end_time are stored as "h:mm A" (e.g. "6:00 PM") by SubEventManager.
          const seStart = moment(`${selectedSe.start_date} ${selectedSe.start_time || "12:00 AM"}`, "YYYY-MM-DD h:mm A");
          const seEnd = moment(`${selectedSe.end_date} ${selectedSe.end_time || "11:59 PM"}`, "YYYY-MM-DD h:mm A");
          subEventConditions = [
            { [dateField]: { _gte: formatDateForApi(seStart) } },
            { [dateField]: { _lt: formatDateForApi(seEnd) } },
          ];
        }

        // Replace any existing date filters with sub-event date range
        if (customQueryPaths?.dateFrom) {
          const dateFromPath = customQueryPaths.dateFrom.split(".")[0];
          const dateToPath = customQueryPaths.dateTo?.split(".")[0];
          if (dateFromPath === "_and" || dateToPath === "_and") {
            where["_and"] = subEventConditions;
          } else {
            delete where[dateFromPath];
            if (dateToPath) delete where[dateToPath];
            where["_and"] = subEventConditions;
          }
        } else {
          where["_and"] = subEventConditions;
        }
      }
    }

    const params = new URLSearchParams();
    params.set("where", JSON.stringify(where));
    if (subEvent) params.set("subEvent", String(subEvent));

    // Already in the address: nothing to do (this also stops a second, identical load).
    const nextSearch = `?${params.toString()}`;
    const isFirstRun = firstFilterEffectRef.current;
    firstFilterEffectRef.current = false;
    if (routerLocation.search === nextSearch) return;
    // The address lost its filter (sidebar click): put it back without adding a history step.
    const reapplying = !hasWhereInUrl;

    if (!isFirstRun && !reapplying) {
      debouncedTrackFilterChange({
        where,
        active_filter_keys: Object.keys(where),
        sub_event_id: subEvent ?? null,
      });
    }

    navigate(nextSearch, { replace: reapplying });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organizationId,
    location,
    dateFrom,
    dateTo,
    organization,
    username,
    productType,
    event,
    vendor,
    excludedVendor,
    subEvent,
    hasWhereInUrl,
  ]);

  const debounceSetDateFrom = debounce((date) => {
    // console.log("debounceSetDateFrom", date);
    setDateFrom(date);
  }, 500);

  const debounceSetDateTo = debounce((date) => {
    // console.log("debounceSetDateTo", date);
    setDateTo(date);
  }, 500);

  // Find the selected organization for the vendor and event dropdowns
  let selectedOrganization = [];
  const startOfYearTimeDefaultValue = includeDatesStartOfYear
      ? dateFromStartOfYear
      : dateFromFiveAm;
  if (multipleOrganizations) {
    if (organization) {
      selectedOrganization = allOrganizations.filter(
          (org: { id: string }) => organization.indexOf(org.id) > -1
      );
    } else {
      selectedOrganization = allOrganizations;
    }
  } else {
    selectedOrganization = organizations;
  }
  const filters = [
    includeDateOnly && includeDates && (
        <>
          <div className="flex flex-row items-center w-full">
            <DatePicker
                label="From"
                granularity="day"
                defaultValue={dateOnlyFrom}
                onChange={debounceSetDateFrom}
                onTimeChange={(time: CalendarDateTime) => {
                  debounceSetDateFrom({ ...dateOnlyFrom, ...time });
                }}
            />
          </div>
          <div className="flex flex-row items-center w-full">
            <DatePicker
                label="To"
                granularity="day"
                defaultValue={dateOnlyTo}
                onChange={debounceSetDateTo}
                onTimeChange={(time: CalendarDateTime) => {
                  debounceSetDateTo({ ...dateOnlyTo, ...time });
                }}
            />
          </div>
        </>
    ),
    includeDates && includeTime && (
        <>
          <div className="flex flex-row items-center w-full">
            <DatePicker
                label="From"
                defaultValue={startOfYearTimeDefaultValue}
                onChange={debounceSetDateFrom}
                onTimeChange={(time: CalendarDateTime) => {
                  debounceSetDateFrom({ ...dateFrom, ...time });
                }}
                includeTime={includeTime}
                granularity="hour"
                hourCycle={12}
            />
          </div>
          <div className="flex flex-row items-center w-full">
            <DatePicker
                label="To"
                defaultValue={dateTo}
                onChange={debounceSetDateTo}
                onTimeChange={(time: CalendarDateTime) => {
                  debounceSetDateTo({ ...dateFrom, ...time });
                }}
                includeTime={includeTime}
                granularity="hour"
                hourCycle={12}
            />
          </div>
        </>
    ),
    includeOrganizations && (
        <div>
          <CustomSelect
              title="Organization"
              multi={multipleOrganizations}
              defaultValue={organization}
              onChange={setOrganization}
              options={renderOrganizationOptions(organizations)}
          />
        </div>
    ),
    includeEvents && (
        <div>
          <CustomSelect
              title="Event"
              multi={multipleEvents}
              defaultValue={event}
              onChange={setEvent}
              isClearable
              options={[
                ...(allEvents?.map((e) => ({
                  label: e.name,
                  value: e.id,
                })) || []),
              ]}
          />
        </div>
    ),
    includeSubEvents && subEventOptions.length > 0 && (
        <div>
          <CustomSelect
              title="Sub-Event"
              defaultValue={subEvent}
              onChange={setSubEvent}
              options={subEventOptions}
              isClearable
          />
        </div>
    ),
    // includeVendors && !user?.vendors && (
    includeVendors && (
        <div>
          <CustomSelect
              title="Vendor"
              defaultValue={vendor}
              onChange={setVendor}
              multi
              closeOnSelect={false}
              options={renderVendorOptions(vendors ?? [])}
          />
        </div>
    ),
    includeVendorExclusions && (
        <div>
          <CustomSelect
              title="Exclude Vendor"
              defaultValue={excludedVendor}
              onChange={setExcludedVendor}
              multi
              closeOnSelect={false}
              options={renderVendorOptions(vendors ?? [])}
          />
        </div>
    ),
    includeLocations && (
        <div>
          <CustomSelect
              title="Location"
              defaultValue={location}
              onChange={setLocation}
              multi
              closeOnSelect={false}
              options={locationOptions ?? []}
          />
        </div>
    ),



    includeProductTypes && (
        <div>
          <CustomSelect
              title={productTypeLabel}
              defaultValue={productType}
              onChange={setProductType}
              multi
              closeOnSelect={false}
              options={productTypeOptions ?? []}
          />
        </div>
    ),
    includeUsernames && (
        // <div className="sm:col-span-3">
        <div>
          <CustomSelect
              title="Username"
              defaultValue={username}
              onChange={setUsername}
              multi
              closeOnSelect={false}
              options={usernameOptions ?? []} // Using passed prop
          />
        </div>
    ),
    includePaymentProcessors && (
        <div className="relative">
          <CheckboxDropdown
              title="Payment Processor"
              options={paymentProcessorOptions ?? []}
              selected={selectedPaymentProcessors ?? null}
              onChange={(value) => onPaymentProcessorChange?.(value)}
          />
        </div>
    ),
  ].filter(Boolean);

  // Grouping changes how rows are presented, and the trailing slot is an
  // action (usually Export to CSV). Neither narrows the data, so they get
  // their own row instead of flowing through the filter grid — otherwise a
  // display control lands between two filters, and its position shifts every
  // time a report enables one filter more or less.
  const displayControls = [
    includeGrouping && (
        <div>
          <CustomSelect
              title="Grouping"
              defaultValue={groupingValue}
              onChange={onGroupingChange}
              options={groupingOptions ?? []}
          />
        </div>
    ),
    trailingSlot && <div className="md:ml-auto">{trailingSlot}</div>,
  ].filter(Boolean);

  return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {filters}
        </div>
        {displayControls.length > 0 && (
            <div className="flex flex-col md:flex-row md:items-end gap-3 mt-3 mb-4 pt-3 border-t border-gray-200">
              {displayControls}
            </div>
        )}
      </>
  );
};
